import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import sanitizeHTML from '../../services/sanitizeHTML';
import { Palette } from 'Palette';
import { StyledTypography } from '../styles/styles';
import { FAQItem } from './types';

// Replace #link:Group[:Question]# placeholders with clickable links after sanitize.
const LINK_REGEX = /#link:([^:#]+)(?::([^#]+))?#/g;

function enhanceLinksInElement(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const toProcess: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const t = n as Text;
    // Reset regex state per text node to avoid global regex lastIndex issues
    LINK_REGEX.lastIndex = 0;
    if (LINK_REGEX.test(t.nodeValue || '')) {
      toProcess.push(t);
    }
  }
  for (const textNode of toProcess) {
    const text = textNode.nodeValue || '';
    const frag = document.createDocumentFragment();
    let lastIndex = 0;
    text.replace(LINK_REGEX, (m, group, question, offset) => {
      // preceding text
      if (offset > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
      }
      const linkText = question ? `${group}: ${question}` : group;
      const a = document.createElement('a');
      a.textContent = linkText;
      a.className = 'faq-link';
      a.href = '#';
      a.dataset.faqGroup = group.trim();
      if (question) a.dataset.faqQuestion = String(question).trim();
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const detail: { group: string; question?: string } = {
          group: a.dataset.faqGroup || '',
          question: a.dataset.faqQuestion,
        };
        window.dispatchEvent(new CustomEvent('faq-link-click', { detail }));
      });
      frag.appendChild(a);
      lastIndex = offset + m.length;
      return m;
    });
    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    textNode.parentNode?.replaceChild(frag, textNode);
  }
}

// Wrap paragraphs that contain an <img> so we can center text vertically
// without breaking inline spacing. We transform:
//   <p>[img][text/inline...]</p>
// into
//   <p class="faq-with-image">[img]<span class="faq-text">[text/inline...]</span></p>
function wrapParagraphsWithImage(root: HTMLElement) {
  const paras = Array.from(root.querySelectorAll('p'));
  for (const p of paras) {
    if ((p as HTMLElement).classList.contains('faq-with-image')) continue;
    const firstEl = p.firstElementChild as HTMLElement | null;
    const hasImg = !!p.querySelector('img');
    if (!hasImg || !firstEl) continue;
    // Allow whitespace before the first element
    let leadingImg: HTMLElement | null = null;
    if (firstEl.tagName.toLowerCase() === 'img') {
      leadingImg = firstEl;
    } else {
      // If first element isn't img, but there is an img and only whitespace precedes it, skip
      const candidate = p.querySelector('img') as HTMLElement | null;
      if (!candidate) continue;
      // Ensure candidate is first non-whitespace node
      let foundNonWs = false;
      for (const node of Array.from(p.childNodes)) {
        if (node === candidate) break;
        if (node.nodeType === Node.TEXT_NODE && (node.textContent || '').trim() === '') continue;
        foundNonWs = true;
        break;
      }
      if (!foundNonWs) leadingImg = candidate;
    }
    if (!leadingImg) continue;

    const wrapper = document.createElement('span');
    wrapper.className = 'faq-text';
    const toMove: ChildNode[] = [];
    for (const node of Array.from(p.childNodes)) {
      if (node === leadingImg) continue;
      toMove.push(node);
    }
    for (const n of toMove) wrapper.appendChild(n);
    p.appendChild(wrapper);
    (p as HTMLElement).classList.add('faq-with-image');
  }
}

export interface FAQItemViewProps {
  item: FAQItem;
  expandable?: boolean;
  defaultExpanded?: boolean;
  searchTerm?: string;
  onToggle?: (expanded: boolean) => void;
}

const highlight = (text: string, searchTerm?: string) => {
  if (!searchTerm || !searchTerm.trim()) return text;
  const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escaped})`, 'gi');
  // Replace matches deterministically (avoid regex.test state)
  return text.replace(
    regex,
    (m) =>
      `<mark style="background-color: #ffeb3b; padding: 2px 4px; border-radius: 3px;">${m}</mark>`,
  );
};

const FAQItemView: React.FC<FAQItemViewProps> = ({
  item,
  expandable = true,
  defaultExpanded = false,
  searchTerm,
  onToggle,
}) => {
  const questionHtml = useMemo(
    () => sanitizeHTML(highlight(item.question, searchTerm)),
    [item.question, searchTerm],
  );
  const answerHtml = useMemo(
    () => sanitizeHTML(highlight(item.answer, searchTerm)),
    [item.answer, searchTerm],
  );

  const qRef = useRef<HTMLDivElement | null>(null);
  const aRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (qRef.current) {
      enhanceLinksInElement(qRef.current);
      wrapParagraphsWithImage(qRef.current);
    }
    if (aRef.current) {
      enhanceLinksInElement(aRef.current);
      wrapParagraphsWithImage(aRef.current);
    }
  }, [questionHtml, answerHtml]);

  if (!expandable) {
    return (
      <Box>
        <StyledTypography
          tone="default"
          fontSize="1rem"
          fontWeight={600}
          dangerouslySetInnerHTML={{ __html: questionHtml }}
          ref={qRef as any}
          sx={{ '& img': { maxWidth: '100%', height: 'auto' } }}
        />
        <StyledTypography
          tone="default"
          fontSize="0.95rem"
          sx={{
            lineHeight: 1.6,
            '& img': {
              maxWidth: '95%',
              marginLeft: 'auto',
              marginRight: 'auto',
              height: 'auto',
            },

            '& ol': {
              paddingLeft: '1.25rem',
              margin: '0.5rem 0',
              listStyleType: 'decimal',
            },
            // Use normal inline flow to preserve spaces around inline tags
            '& li': { marginBottom: '0.25rem' },
            // Center text next to images using wrapper created at runtime
            '& p.faq-with-image': {
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            },
            '& p.faq-with-image > img': { flex: '0 0 auto' },
            '& p.faq-with-image > .faq-text': { flex: 1, minWidth: 0 },
            '& em, & i, & a': { display: 'inline' },
            '& a': {
              color: Palette['default'].buttonText,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
              className: 'react-StyledLink',
            },
          }}
          dangerouslySetInnerHTML={{ __html: answerHtml }}
          ref={aRef as any}
        />
      </Box>
    );
  }

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      onChange={(_, expanded) => onToggle?.(expanded)}
      sx={{
        backgroundColor: 'grey.50',
        borderRadius: 1,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'grey.200',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '4px 0' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{ '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
      >
        <StyledTypography
          tone="default"
          fontSize="1rem"
          fontWeight={500}
          dangerouslySetInnerHTML={{ __html: questionHtml }}
          ref={qRef as any}
          sx={{ '& img': { maxWidth: '100%', height: 'auto' } }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <StyledTypography
          tone="default"
          fontSize="0.95rem"
          sx={{
            lineHeight: 1.6,
            '& img': {
              maxWidth: '95%',
              marginLeft: 'auto',
              marginRight: 'auto',
              height: 'auto',
            },
            '& ol': {
              paddingLeft: '1.25rem',
              margin: '0.5rem 0',
              listStyleType: 'decimal',
            },
            '& li': { marginBottom: '0.25rem' },
            // For list items keep icon float for wrap, but center using wrapper if present
            '& li img': { marginRight: '1rem', float: 'left' },
            '& p.faq-with-image': {
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            },
            '& p.faq-with-image > img': { flex: '0 0 auto' },
            '& p.faq-with-image > .faq-text': { flex: 1, minWidth: 0 },
            '& em, & i, & a': { display: 'inline' },
            '& a': {
              color: Palette['tone1'].buttonText,
              fontWeight: 600,
              '&:hover': {
                textDecoration: 'underline',
              },
              className: 'react-StyledLink',
            },
          }}
          dangerouslySetInnerHTML={{ __html: answerHtml }}
          ref={aRef as any}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default FAQItemView;
